/**
 * Generates a UUID v4 string using crypto.randomUUID().
 *
 * @returns A UUID v4 string (e.g., "550e8400-e29b-41d4-a716-446655440000")
 */
export const generateUUID = (): string => {
  return crypto.randomUUID()
}

import type {
  Tab,
  Suggestion,
  FlowMode,
} from "~/react/components/email_accounts/types"

export const toSlug = (text: string): string =>
  text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ".")
    .replace(/^\.+|\.+$/g, "")

/**
 * Generates local-part patterns for email addresses in order of commonality.
 * Creates variations like first, last, first.last, first_last, etc.
 *
 * @param first - First name
 * @param last - Last name
 * @param max - Maximum number of patterns to return
 * @returns Array of unique local-part patterns
 */
export const generateLocalParts = (
  first: string,
  last: string,
  max: number,
): string[] => {
  const f = (first || "").trim()
  const l = (last || "").trim()
  const fSlug = toSlug(f)
  const lSlug = toSlug(l)
  const fFirst = fSlug[0] || ""
  const lFirst = lSlug[0] || ""
  const parts: string[] = []

  if (fSlug) parts.push(fSlug)
  if (lSlug) parts.push(lSlug)
  if (fFirst && lSlug) parts.push(`${fFirst}${lSlug}`)
  if (fSlug && lSlug) parts.push(`${fSlug}.${lSlug}`)
  if (fSlug && lSlug) parts.push(`${fSlug}_${lSlug}`)
  if (fSlug && lSlug) parts.push(`${fSlug}-${lSlug}`)
  if (fFirst && lSlug) parts.push(`${fFirst}.${lSlug}`)
  if (fSlug && lSlug) parts.push(`${fSlug}${lSlug}`)
  if (lSlug && fSlug) parts.push(`${lSlug}.${fSlug}`)
  if (lSlug && fSlug) parts.push(`${lSlug}_${fSlug}`)
  if (lSlug && fSlug) parts.push(`${lSlug}-${fSlug}`)
  if (lSlug && fSlug) parts.push(`${lSlug}${fSlug}`)
  if (fFirst && lSlug) parts.push(`${fFirst}_${lSlug}`)
  if (fFirst && lSlug) parts.push(`${fFirst}-${lSlug}`)
  if (fSlug && lFirst) parts.push(`${fSlug}.${lFirst}`)
  if (fSlug && lFirst) parts.push(`${fSlug}_${lFirst}`)
  if (fSlug && lFirst) parts.push(`${fSlug}-${lFirst}`)
  if (fSlug && lFirst) parts.push(`${fSlug}${lFirst}`)
  if (fFirst && lFirst) parts.push(`${fFirst}${lFirst}`)
  const seen = new Set<string>()
  const uniques: string[] = []
  for (const p of parts) {
    const key = p.toLowerCase()
    if (!key || seen.has(key)) continue
    seen.add(key)
    uniques.push(key)
    if (uniques.length >= max) break
  }
  return uniques
}

/**
 * Checks if a signature string has actual content (not just empty HTML).
 *
 * @param sig - Signature HTML string
 * @returns true if signature has non-empty text content
 */
export const hasSignatureContent = (sig: string | undefined): boolean => {
  if (!sig) return false
  const trimmed = sig.trim()
  if (trimmed.length === 0) return false
  // Strip HTML tags and check if there's actual text content
  const textContent = trimmed.replace(/<[^>]*>/g, "").trim()
  return textContent.length > 0
}

export const getStorageKey = (flowMode?: FlowMode): string => {
  if (flowMode === "domains_only") {
    return "email_accounts_provisioning_domains_form"
  }
  if (flowMode === "mailboxes_only") {
    return "email_accounts_provisioning_mailboxes_form"
  }
  return "email_accounts_provisioning_form"
}

export const TAB_ORDER: Tab[] = ["domain", "accounts", "billing", "checkout"]

export const getTabOrder = (flowMode?: FlowMode): Tab[] => {
  if (flowMode === "domains_only") {
    return ["domain", "billing", "checkout"]
  }
  if (flowMode === "mailboxes_only") {
    return ["domain", "accounts", "billing", "checkout"]
  }
  return TAB_ORDER
}

/**
 * Gets a Set of existing domain names from suggestions, normalized to lowercase.
 * Only includes domains from suggestions with status "ready".
 *
 * @param suggestions - Array of domain suggestions
 * @param excludeIds - Optional Set of suggestion IDs to exclude from the result
 * @returns Set of normalized domain names (lowercase)
 */
export const getExistingDomains = (
  suggestions: Suggestion[],
  excludeIds?: Set<string>,
): Set<string> => {
  return new Set(
    suggestions
      .filter(
        (s) => s.status === "ready" && (!excludeIds || !excludeIds.has(s.id)),
      )
      .map((s) => s.domain?.toLowerCase())
      .filter(Boolean) as string[],
  )
}

/**
 * Checks if a domain already exists in the suggestions list.
 *
 * @param suggestions - Array of domain suggestions to check against
 * @param domain - The domain to check (will be normalized to lowercase)
 * @returns true if the domain already exists in ready suggestions, false otherwise
 */
export const isDuplicateDomain = (
  suggestions: Suggestion[],
  domain: string,
): boolean => {
  const normalized = domain.trim().toLowerCase()
  return suggestions.some(
    (s) =>
      s.status === "ready" && (s.domain || "").toLowerCase() === normalized,
  )
}

/**
 * Filters out duplicate domains from a new list of suggestions based on existing suggestions.
 *
 * @param newSuggestions - Array of new domain suggestions to filter
 * @param existingSuggestions - Array of existing suggestions to check against
 * @param excludeIds - Optional Set of suggestion IDs to exclude from existing domains check
 * @returns Filtered array of suggestions without duplicates
 */
export const filterDuplicateDomains = <T extends { domain?: string }>(
  newSuggestions: T[],
  existingSuggestions: Suggestion[],
  excludeIds?: Set<string>,
): T[] => {
  const existingDomains = getExistingDomains(existingSuggestions, excludeIds)
  return newSuggestions.filter((item) => {
    const normalizedDomain = item.domain?.toLowerCase()
    return normalizedDomain ? !existingDomains.has(normalizedDomain) : false
  })
}

/**
 * Merges new domain suggestions into placeholder suggestions, filling placeholders
 * with available suggestions and removing any unfilled placeholders.
 *
 * @param currentSuggestions - Current array of suggestions (including placeholders)
 * @param placeholderIds - Set of placeholder suggestion IDs to fill
 * @param newSuggestions - Array of new domain suggestions from the API
 * @returns Updated array of suggestions with placeholders filled or removed
 */
export const mergeSuggestionsIntoPlaceholders = (
  currentSuggestions: Suggestion[],
  placeholderIds: Set<string>,
  newSuggestions: Array<{ domain: string; price_cents: number }>,
): Suggestion[] => {
  const next = [...currentSuggestions]

  const indices = next
    .map((s, idx) => ({ s, idx }))
    .filter(({ s }) => s.status === "loading" && placeholderIds.has(s.id))
    .map(({ idx }) => idx)

  const items = filterDuplicateDomains(newSuggestions, next, placeholderIds)

  // Fill as many as we can from the filtered suggestions
  indices.forEach((idx, i) => {
    const item = items[i]
    if (item) {
      next[idx] = {
        id: next[idx].id,
        status: "ready",
        domain: item.domain,
        priceCents: item.price_cents,
      }
    }
  })

  const filledCount = Math.min(items.length, indices.length)
  if (filledCount < indices.length) {
    const idsToRemove = new Set(
      indices.slice(filledCount).map((i) => next[i].id),
    )
    return next.filter(
      (s) => !(s.status === "loading" && idsToRemove.has(s.id)),
    )
  }

  return next
}

/**
 * Gets an appropriate error message based on HTTP response status code.
 * First tries to extract an error message from the response body, then falls back
 * to status-code-specific messages.
 *
 * @param response - The fetch Response object
 * @param defaultMessage - Default error message if status code doesn't match known codes
 * @returns Promise resolving to an error message string
 */
export const getErrorMessageFromResponse = async (
  response: Response,
  defaultMessage: string,
): Promise<string> => {
  const contentType = response.headers.get("content-type")
  if (contentType?.includes("application/json")) {
    try {
      // Clone the response to avoid consuming the original stream
      const clonedResponse = response.clone()
      const data = await clonedResponse.json()
      if (data.error) {
        // Handle specific error codes with user-friendly messages
        if (data.error === "mailpool_error") {
          return i18n.t("js.email_accounts.provisioning.mailpool_error")
        }
        return data.error
      }
      if (data.message) return data.message
      if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
        return data.errors[0]
      }
    } catch {}
  }

  switch (response.status) {
    case 400:
      return i18n.t("js.email_accounts.provisioning.bad_request_error")
    case 401:
      return i18n.t("js.email_accounts.provisioning.unauthorized_error")
    case 403:
      return i18n.t("js.email_accounts.provisioning.forbidden_error")
    case 404:
      return i18n.t("js.email_accounts.provisioning.not_found_error")
    case 500:
    case 502:
    case 503:
    case 504:
      return i18n.t("js.email_accounts.provisioning.server_error")
    default:
      return defaultMessage
  }
}

/**
 * Handles fetch errors and network errors, providing appropriate error messages.
 *
 * @param error - The error object (could be Error, TypeError, etc.)
 * @param defaultMessage - Default error message for unknown errors
 * @returns Error message string
 */
export const getErrorMessageFromError = (
  error: unknown,
  defaultMessage: string,
): string => {
  if (error instanceof TypeError && error.message.includes("fetch")) {
    return i18n.t("js.email_accounts.provisioning.network_error")
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  return defaultMessage
}

